import type { CSSProperties, DragEvent } from 'react';
import { useState } from 'react';
import { useDemo2Ctx } from '../Demo2Context';
import { ZONES, WORKERS, TYPE_ICON_RI, type WorkOrder, type WorkType, type WorkPriority, type WorkStatus } from '../demo2Data';

const COL_THEME: Record<WorkStatus, { bg: string; line: string; tone: string; cntBg: string }> = {
    waiting: { bg: '#F1F5F9', line: '#E2E8F0', tone: '#475569', cntBg: '#E2E8F0' },
    progress: { bg: '#EFF6FF', line: '#BFDBFE', tone: '#1D4ED8', cntBg: '#DBEAFE' },
    done: { bg: '#ECFDF5', line: '#A7F3D0', tone: '#047857', cntBg: '#D1FAE5' },
};

function initials(name: string) { return name.slice(0, 1); }

const COL_LABEL: Record<WorkStatus, string> = { waiting: '대기', progress: '진행중', done: '완료' };

function NewWorkOrderForm({ onClose }: { onClose: () => void }) {
    const { createWorkOrder, toast } = useDemo2Ctx();
    const [zone, setZone] = useState(ZONES[0].id);
    const [type, setType] = useState<WorkType>('관개');
    const [priority, setPriority] = useState<WorkPriority>('normal');
    const [assignee, setAssignee] = useState(WORKERS[1].name);
    const [due, setDue] = useState('2026-07-21');

    const submit = () => {
        const id = createWorkOrder({ zone, type, assignee, due, priority });
        onClose();
        toast(`${id} 작업지시가 생성되어 모바일 앱으로 전송되었습니다 ✓`, 'success');
    };

    return (
        <>
            <div className="rpnl-body">
                <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="form-label">대상 관리구역 <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <select className="form-select" value={zone} onChange={e => setZone(e.target.value)}>
                        {ZONES.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                    </select>
                </div>
                <div className="form-row" style={{ marginBottom: 14 }}>
                    <div className="form-group">
                        <label className="form-label">작업 종류</label>
                        <select className="form-select" value={type} onChange={e => setType(e.target.value as WorkType)}>
                            <option>관개</option><option>전정</option><option>방제</option><option>시비</option><option>배수개선</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">우선순위</label>
                        <select className="form-select" value={priority} onChange={e => setPriority(e.target.value as WorkPriority)}>
                            <option value="urgent">긴급</option><option value="high">높음</option><option value="normal">보통</option>
                        </select>
                    </div>
                </div>
                <div className="form-row" style={{ marginBottom: 14 }}>
                    <div className="form-group">
                        <label className="form-label">작업자 지정</label>
                        <select className="form-select" value={assignee} onChange={e => setAssignee(e.target.value)}>
                            {WORKERS.map(w => <option key={w.name}>{w.name}</option>)}
                        </select>
                    </div>
                    <div className="form-group"><label className="form-label">예정일</label><input type="date" className="form-input" value={due} onChange={e => setDue(e.target.value)} /></div>
                </div>
                <div className="form-group"><label className="form-label">작업 지시 메모</label><textarea className="form-textarea" placeholder="현장 특이사항, 관개량 목표 등을 기재하세요" /></div>
            </div>
            <div className="rpnl-ft">
                <button className="btn btn-ghost" onClick={onClose}>취소</button>
                <button className="btn btn-primary" onClick={submit}>작업지시 생성 → 모바일 전송</button>
            </div>
        </>
    );
}

function EditWorkOrderForm({ wo, onClose }: { wo: WorkOrder; onClose: () => void }) {
    const { updateWorkOrder, toast } = useDemo2Ctx();
    const [zone, setZone] = useState(wo.zone);
    const [type, setType] = useState<WorkType>(wo.type);
    const [priority, setPriority] = useState<WorkPriority>(wo.priority);
    const [assignee, setAssignee] = useState(wo.assignee);
    const [due, setDue] = useState(wo.due);

    const submit = () => {
        updateWorkOrder(wo.id, { zone, type, assignee, due, priority });
        onClose();
        toast(`${wo.id} 작업내용이 수정되었습니다 ✓`, 'success');
    };

    return (
        <>
            <div className="rpnl-body">
                <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="form-label">대상 관리구역 <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <select className="form-select" value={zone} onChange={e => setZone(e.target.value)}>
                        {ZONES.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                    </select>
                </div>
                <div className="form-row" style={{ marginBottom: 14 }}>
                    <div className="form-group">
                        <label className="form-label">작업 종류</label>
                        <select className="form-select" value={type} onChange={e => setType(e.target.value as WorkType)}>
                            <option>관개</option><option>전정</option><option>방제</option><option>시비</option><option>배수개선</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">우선순위</label>
                        <select className="form-select" value={priority} onChange={e => setPriority(e.target.value as WorkPriority)}>
                            <option value="urgent">긴급</option><option value="high">높음</option><option value="normal">보통</option>
                        </select>
                    </div>
                </div>
                <div className="form-row" style={{ marginBottom: 14 }}>
                    <div className="form-group">
                        <label className="form-label">작업자 지정</label>
                        <select className="form-select" value={assignee} onChange={e => setAssignee(e.target.value)}>
                            {WORKERS.map(w => <option key={w.name}>{w.name}</option>)}
                        </select>
                    </div>
                    <div className="form-group"><label className="form-label">예정일</label><input type="date" className="form-input" value={due} onChange={e => setDue(e.target.value)} /></div>
                </div>
                <div className="form-group">
                    <label className="form-label">현재 상태</label>
                    <div className="text-xs text-muted">{COL_LABEL[wo.status]}{wo.status === 'done' && wo.completedAt ? ` · ${wo.completedAt} 완료` : ''}</div>
                </div>
            </div>
            <div className="rpnl-ft">
                <button className="btn btn-ghost" onClick={onClose}>취소</button>
                <button className="btn btn-primary" onClick={submit}>수정 내용 저장</button>
            </div>
        </>
    );
}

export default function Demo2WorkManagement() {
    const { workOrders, startWorkOrder, completeWorkOrder, moveWorkOrderStatus, openPanel, closePanel, toast } = useDemo2Ctx();
    const [doneTarget, setDoneTarget] = useState<WorkOrder | null>(null);
    const [resultNote, setResultNote] = useState('');
    const [dragId, setDragId] = useState<string | null>(null);
    const [dragOverCol, setDragOverCol] = useState<WorkStatus | null>(null);

    const groups: Record<WorkStatus, WorkOrder[]> = { waiting: [], progress: [], done: [] };
    workOrders.forEach(w => groups[w.status].push(w));

    const openNewPanel = () => openPanel({ title: '＋ 새 작업지시 생성', body: <NewWorkOrderForm onClose={closePanel} /> });

    const start = (w: WorkOrder) => {
        startWorkOrder(w.id);
        toast('작업이 시작되었습니다 · 담당자 모바일 앱에 반영됨', 'success');
    };
    const openDone = (w: WorkOrder) => { setDoneTarget(w); setResultNote(''); };
    const confirmDone = () => {
        if (!doneTarget) return;
        completeWorkOrder(doneTarget.id, resultNote || '작업 완료');
        toast('작업이 완료 처리되었습니다 · 이력에 자동 등록됨', 'success');
        setDoneTarget(null);
    };

    const handleDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
        setDragId(id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
    };
    const handleDragEnd = () => { setDragId(null); setDragOverCol(null); };
    const handleColDragOver = (e: DragEvent<HTMLDivElement>, col: WorkStatus) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverCol !== col) setDragOverCol(col);
    };
    const handleColDragLeave = (col: WorkStatus) => {
        setDragOverCol(prev => (prev === col ? null : prev));
    };
    const handleColDrop = (e: DragEvent<HTMLDivElement>, col: WorkStatus) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('text/plain') || dragId;
        setDragOverCol(null);
        setDragId(null);
        if (!id) return;
        const w = workOrders.find(x => x.id === id);
        if (!w || w.status === col) return;
        if (col === 'done') {
            openDone(w);
        } else {
            moveWorkOrderStatus(id, col);
            toast(`'${w.title}' → ${COL_LABEL[col]}(으)로 이동`, 'success');
        }
    };

    const renderCard = (w: WorkOrder) => {
        const worker = WORKERS.find(x => x.name === w.assignee);
        return (
            <div
                key={w.id}
                className={`kanban-card${dragId === w.id ? ' dragging' : ''}`}
                draggable
                onDragStart={e => handleDragStart(e, w.id)}
                onDragEnd={handleDragEnd}
                onClick={() => openPanel({ title: `✎ 작업내용 수정 · ${w.id}`, body: <EditWorkOrderForm wo={w} onClose={closePanel} /> })}
            >
                <div className="kc-top">
                    <span className="kc-title"><i className={TYPE_ICON_RI[w.type]} /> {w.title}</span>
                    <span className={`badge badge-${w.priority === 'urgent' ? 'danger' : w.priority === 'high' ? 'warn' : 'neutral'}`} style={{ flexShrink: 0 }}>
                        {w.priority === 'urgent' ? '긴급' : w.priority === 'high' ? '높음' : '보통'}
                    </span>
                </div>
                <div className="text-xs text-muted">{w.zoneName}</div>
                <div className="kc-meta">
                    <span className="kc-worker"><span className="kc-worker-av" style={{ background: worker?.grad ?? '#8AA88A' }}>{initials(w.assignee)}</span>{w.assignee}</span>
                    <span><i className="ri-calendar-line" /> {w.due}</span>
                </div>
                {w.status === 'waiting' && <button className="btn btn-secondary btn-sm w-full mt-2" onClick={e => { e.stopPropagation(); start(w); }}><i className="ri-play-line" /> 작업 시작</button>}
                {w.status === 'progress' && <button className="btn btn-primary btn-sm w-full mt-2" onClick={e => { e.stopPropagation(); openDone(w); }}><i className="ri-check-line" /> 완료 처리</button>}
                {w.status === 'done' && <div className="text-xs mt-2" style={{ color: 'var(--good)' }}><i className="ri-checkbox-circle-line" /> {w.completedAt} 완료</div>}
            </div>
        );
    };

    const renderCol = (col: WorkStatus, dotColor: string) => {
        const theme = COL_THEME[col];
        return (
            <div
                className={`kanban-col${dragOverCol === col ? ' drag-over' : ''}`}
                onDragOver={e => handleColDragOver(e, col)}
                onDragLeave={() => handleColDragLeave(col)}
                onDrop={e => handleColDrop(e, col)}
            >
                <div className="kanban-col-hd" style={{ '--col-bg': theme.bg, '--col-line': theme.line, '--col-tone': theme.tone, '--col-cnt-bg': theme.cntBg } as CSSProperties}>
                    <span className="kanban-col-ttl"><span className="kanban-col-dot" style={{ background: dotColor }} />{COL_LABEL[col]}</span>
                    <span className="kanban-col-cnt">{groups[col].length}</span>
                </div>
                <div className="kanban-body">{groups[col].length ? groups[col].map(renderCard) : <div className="text-xs text-muted" style={{ textAlign: 'center', padding: '20px 0' }}>{COL_LABEL[col]} 작업 없음</div>}</div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className="list-bar">
                <div style={{ fontSize: 13.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7 }}><i className="ri-file-list-3-line" /> 관개·관리 작업 지시</div>
                <div style={{ flex: 1 }} />
                <span className="text-xs text-muted">카드를 드래그해 상태를 옮길 수 있습니다</span>
                <span className="text-xs text-muted">전체 {workOrders.length}건 · 완료율 {Math.round((groups.done.length / workOrders.length) * 100)}%</span>
                <button className="btn btn-primary btn-sm" onClick={openNewPanel}><i className="ri-add-line" /> 작업지시 생성</button>
            </div>
            <div className="kanban-wrap" style={{ flex: 1 }}>
                {renderCol('waiting', COL_THEME.waiting.tone)}
                {renderCol('progress', COL_THEME.progress.tone)}
                {renderCol('done', COL_THEME.done.tone)}
            </div>

            <div className={`sian sian-modal-overlay${doneTarget ? ' open' : ''}`} onClick={() => setDoneTarget(null)}>
                <div className="sian-modal" onClick={e => e.stopPropagation()}>
                    <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>작업 완료 처리</div>
                    <div className="text-xs text-muted" style={{ marginBottom: 16 }}>완료 내용을 입력하면 이력에 자동 등록되고 Web 대시보드에 실시간 반영됩니다.</div>
                    <div className="form-group" style={{ marginBottom: 14 }}>
                        <label className="form-label">작업 결과</label>
                        <textarea className="form-textarea" placeholder="예: 관개량 1,200L, 관개 후 수분율 68% 회복" value={resultNote} onChange={e => setResultNote(e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setDoneTarget(null)}>취소</button>
                        <button className="btn btn-primary" style={{ flex: 2 }} onClick={confirmDone}>완료 처리</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
